import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { BookMetadata } from './types';
import { buildReference, referenceFilename, type ReferenceFormat } from '../../utils/citation';
import { downloadTextFile } from '../../utils/clipboard';
import { useTranslation } from '../../i18n/useTranslation';

interface ExportReferenceModalProps {
  bookMetadata: BookMetadata;
  locationLabel?: string;
  onClose: () => void;
}

export default function ExportReferenceModal({
  bookMetadata,
  locationLabel,
  onClose,
}: ExportReferenceModalProps) {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ReferenceFormat>('ris');
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  if (previousFocusRef.current === null && typeof document !== 'undefined') {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
  }
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.focus();
    return () => {
      requestAnimationFrame(() => {
        if (!dialog?.isConnected && previousFocusRef.current) {
          previousFocusRef.current.focus?.();
        }
      });
    };
  }, []);

  const handleExport = () => {
    const content = buildReference(format, { metadata: bookMetadata, locationLabel });
    const filename = referenceFilename(format, bookMetadata, locationLabel);
    const mime = format === 'ris' ? 'application/x-research-info-systems' : 'application/x-endnote-refer';
    downloadTextFile(filename, content, mime);
    onClose();
  };

  return (
    <div
      role="presentation"
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-reference-title"
        tabIndex={-1}
        className="bg-white rounded-lg shadow-2xl max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 id="export-reference-title" className="text-base font-semibold text-neutral-800">{t('App.Reader.ExportReference.Title')}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-neutral-100 rounded transition-colors"
            aria-label={t('App.Reader.ExportReference.CloseLabel')}
          >
            <X className="w-5 h-5 text-neutral-500" aria-hidden="true" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div role="radiogroup" aria-label={t('App.Reader.ExportReference.FormatLabel')} className="grid grid-cols-2 gap-3">
            <FormatOption
              label={t('App.Reader.ExportReference.RefworksLabel')}
              selected={format === 'ris'}
              onClick={() => setFormat('ris')}
            />
            <FormatOption
              label={t('App.Reader.ExportReference.EndnoteLabel')}
              selected={format === 'endnote'}
              onClick={() => setFormat('endnote')}
            />
          </div>

          <button
            onClick={handleExport}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            {t('App.Reader.ExportReference.ButtonLabel')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FormatOptionProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

function FormatOption({ label, selected, onClick }: FormatOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 border-2 rounded-lg transition-colors text-sm ${
        selected
          ? 'border-primary-600 text-primary-700 bg-primary-50'
          : 'border-neutral-300 text-neutral-700 hover:border-neutral-400'
      }`}
    >
      <span
        aria-hidden="true"
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
          selected ? 'border-primary-600' : 'border-neutral-400'
        }`}
      >
        {selected && <span className="w-2 h-2 rounded-full bg-primary-600" />}
      </span>
      {label}
    </button>
  );
}
